"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Cookies from "js-cookie";
import { Lock, User, AlertCircle, FileText, Calendar, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PortalFuncionarioLogin() {
  const router = useRouter();
  const [modoReset, setModoReset] = useState(false);
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 11) value = value.slice(0, 11);
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    setCpf(value);
  };

  const handleDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 8) value = value.slice(0, 8);
    value = value.replace(/(\d{2})(\d)/, "$1/$2");
    value = value.replace(/(\d{2})(\d)/, "$1/$2");
    setDataNascimento(value);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setErro(""); setCarregando(true);
    try {
      const response = await api.post("/portal/login", { cpf, senha });
      const { token, usuario } = response.data;
      Cookies.set("hmdocs_token", token, { expires: 7 }); 
      localStorage.setItem("hmdocs_user", JSON.stringify(usuario));
      router.push("/meus-documentos");
    } catch (err: any) { setErro(err.response?.data?.error || "Erro de conexão."); } 
    finally { setCarregando(false); }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault(); setErro(""); setSucesso(""); setCarregando(true);
    try {
      const response = await api.post("/portal/resetar-senha", { cpf, dataNascimento, novaSenha });
      setSucesso(response.data.message);
      setTimeout(() => { setSenha(""); setModoReset(false); setSucesso(""); }, 3000);
    } catch (err: any) { setErro(err.response?.data?.error || "Não foi possível redefinir a senha."); } 
    finally { setCarregando(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4 font-sans text-zinc-900">
      <div className="w-full max-w-sm bg-white rounded-[2rem] shadow-xl border border-zinc-100 overflow-hidden transition-all duration-300">
        
        <div className="bg-zinc-950 p-8 text-center relative overflow-hidden">
          {/* Efeito sutil no fundo */}
          <div className="absolute top-[-20%] right-[-10%] w-32 h-32 bg-zinc-800 rounded-full mix-blend-screen filter blur-[40px] opacity-60"></div>

          {modoReset && (
            <button onClick={() => { setModoReset(false); setErro(""); setSucesso(""); }} className="absolute top-4 left-4 text-zinc-400 hover:text-white transition p-2 z-10">
              <ArrowLeft className="h-6 w-6" />
            </button>
          )}
          
          <div className="bg-zinc-800/50 p-3.5 rounded-2xl inline-block mb-4 border border-zinc-800 relative z-10">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight relative z-10">
            {modoReset ? "Recuperar Acesso" : "Portal do Colaborador"}
          </h1>
        </div>

        <div className="p-8">
          {!modoReset ? (
            <form onSubmit={handleLogin} className="space-y-6 animate-in fade-in slide-in-from-left-4">
              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-2">Seu CPF</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><User className="h-5 w-5 text-zinc-400" /></div>
                  <input type="text" required value={cpf} onChange={handleCpfChange} className="block w-full h-14 pl-12 pr-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-zinc-950 outline-none transition text-lg font-medium text-zinc-900 placeholder-zinc-400" placeholder="000.000.000-00" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-2">Senha</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Lock className="h-5 w-5 text-zinc-400" /></div>
                  <input type="password" required value={senha} onChange={(e) => setSenha(e.target.value)} className="block w-full h-14 pl-12 pr-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-zinc-950 outline-none transition text-lg font-medium text-zinc-900 placeholder-zinc-400" placeholder="••••••••" />
                </div>
              </div>

              {erro && (
                <div className="bg-red-50 border border-red-200 p-3.5 rounded-xl flex items-start gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" /><span>{erro}</span>
                </div>
              )}

              <button type="submit" disabled={carregando} className="w-full flex justify-center items-center gap-2 h-14 bg-zinc-950 hover:bg-zinc-800 text-white rounded-2xl text-base font-bold transition-all active:scale-[0.98] disabled:bg-zinc-300 disabled:text-zinc-500 shadow-md">
                {carregando ? <><Loader2 className="h-5 w-5 animate-spin" /> Conectando...</> : "Entrar no Portal"}
              </button>

              <div className="text-center pt-3">
                <button type="button" onClick={() => { setModoReset(true); setErro(""); }} className="text-sm font-bold text-zinc-500 hover:text-zinc-900 transition">
                  Esqueci minha senha
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-5 animate-in fade-in slide-in-from-right-4">
              <p className="text-sm font-medium text-zinc-500 mb-4 text-center">
                Confirme seus dados cadastrados pelo RH para redefinir sua senha de acesso.
              </p>

              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-1.5">Seu CPF</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><User className="h-5 w-5 text-zinc-400" /></div>
                  <input type="text" required value={cpf} onChange={handleCpfChange} className="block w-full h-12 pl-12 pr-4 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-950 outline-none transition font-medium" placeholder="000.000.000-00" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-1.5">Data de Nascimento</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Calendar className="h-5 w-5 text-zinc-400" /></div>
                  <input type="text" required value={dataNascimento} onChange={handleDataChange} className="block w-full h-12 pl-12 pr-4 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-950 outline-none transition font-medium" placeholder="DD/MM/AAAA" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-zinc-700 mb-1.5">Nova Senha</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Lock className="h-5 w-5 text-zinc-400" /></div>
                  <input type="text" required value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} className="block w-full h-12 pl-12 pr-4 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-950 outline-none transition font-medium" placeholder="Crie uma nova senha" />
                </div>
              </div>

              {erro && (
                <div className="bg-red-50 border border-red-200 p-3.5 rounded-xl flex items-start gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" /><span>{erro}</span>
                </div>
              )}

              {sucesso && (
                <div className="bg-emerald-50 border border-emerald-200 p-3.5 rounded-xl flex items-start gap-2 text-emerald-700 text-sm font-bold">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" /><span>{sucesso}</span>
                </div>
              )}

              <button type="submit" disabled={carregando || sucesso !== ""} className="w-full flex justify-center items-center gap-2 h-12 mt-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl text-base font-bold transition-all active:scale-[0.98] disabled:bg-zinc-300 disabled:text-zinc-500 shadow-md">
                {carregando ? <><Loader2 className="h-5 w-5 animate-spin" /> Validando...</> : sucesso ? "Redirecionando..." : "Redefinir Senha"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
